import { __assign } from "tslib";
import { defaultStatusCodeCheck, logDefaultServerError, singleton } from '@sky-uk-ott/client-lib-js-ott-utils';
import { throwOvpErrors } from '../../utils/error-utils';
import { config } from '../vpip/playback-config-storage';
import { getEventContentPayload, getLinearContentPayload, getShortformContentPayload, getVodContentPayload } from './content-request';
import heartbeatRequest from './heartbeat-request';
import { UnknownDeviceIdError } from './playback-service-errors';
import stopStreamRequest from './stop-stream-request';
import { H265, addH265DeviceCapabilities, filterOutProtected } from './video-device-capabilities-utils';
/**
 * An **optional** companion to the PlaybackService `getRequestOptions`.
 *
 * Clients can import it and pass down in `getRequestOptions` or compose another based on this recipe.
 *
 * @param {Object} requestOptions - Extension of CVSDK's OvpPlayoutRequestOptions
 * @param {String} context - Context for the
 * @returns {Object} requestOptions - Copy of `requestOptions` with modified `capabilities`
 */
export var modifyCapabilities = function (reqOptions, context) {
    var _a;
    var capabilities = reqOptions.capabilities || [];
    if (context === 'shortform') {
        capabilities = filterOutProtected(capabilities);
    }
    if ((_a = reqOptions.supportedVideoCodecs) === null || _a === void 0 ? void 0 : _a.includes(H265)) {
        capabilities = addH265DeviceCapabilities(capabilities);
    }
    return __assign(__assign({}, reqOptions), { capabilities: capabilities });
};
var PlaybackService = /** @class */ (function () {
    function PlaybackService(_a) {
        var request = _a.request, ovpTokenService = _a.ovpTokenService, logger = _a.logger, getRequestOptions = _a.getRequestOptions;
        var handleDeviceIdError = throwOvpErrors([
            {
                httpStatus: 403,
                ovpErrorCode: "OVP_00311" /* OttErrorCode.UNKNOWN_DEVICE_ID */,
                errorClass: UnknownDeviceIdError,
            },
        ]);
        var configureRequest = function (requestConfiguratorFunction) {
            return function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var _a = requestConfiguratorFunction.apply(void 0, args), generatedEndPoint = _a[0], requestOptions = _a[1];
                return request(generatedEndPoint, requestOptions);
            };
        };
        var logging = function (data) { return logDefaultServerError(data, 'OVP - PlaybackService', logger); };
        var frozenConfig = function () {
            var _a, _b, _c, _d, _e;
            return (__assign(__assign({}, config), { headers: __assign(__assign({}, config.ovpHeaders), { activeTerritory: (_a = config.activeTerritory) === null || _a === void 0 ? void 0 : _a.call(config), customHeaders: (_b = config.customHeaders) === null || _b === void 0 ? void 0 : _b.call(config), language: (_c = config.language) === null || _c === void 0 ? void 0 : _c.call(config), country: (_d = config.countryCode) === null || _d === void 0 ? void 0 : _d.call(config), postalCode: (_e = config.postalCode) === null || _e === void 0 ? void 0 : _e.call(config) }) }));
        };
        /**
         * Wraps the `requestFunction` and:
         * - Normalizes the public API
         * - Injects `config` into the inner functions
         * - Modifies the `requestOptions`
         */
        var wrapCall = function (requestFunction, contentContext, isContent) {
            return function (reqOptions) {
                var patchedOptions = (getRequestOptions === null || getRequestOptions === void 0 ? void 0 : getRequestOptions(reqOptions, contentContext)) || reqOptions;
                var req = requestFunction(frozenConfig(), patchedOptions);
                if (isContent) {
                    req = req.then(handleDeviceIdError);
                }
                return req.then(logging).then(defaultStatusCodeCheck);
            };
        };
        // Wrap them in tokenize so the token argument (first) gets trimmed and always provided
        var tokenizedEventRequest = ovpTokenService.tokenize(configureRequest(getEventContentPayload));
        var tokenizedVODRequest = ovpTokenService.tokenize(configureRequest(getVodContentPayload));
        var tokenizedLiveRequest = ovpTokenService.tokenize(configureRequest(getLinearContentPayload));
        var tokenizedHeartbeatRequest = ovpTokenService.tokenize(configureRequest(heartbeatRequest));
        var tokenizedStopStreamRequest = ovpTokenService.tokenize(configureRequest(stopStreamRequest));
        // Public API. For content requests, enforce pin
        this.getShortFormAsset = wrapCall(configureRequest(getShortformContentPayload), 'shortform', true);
        this.getEventAsset = wrapCall(tokenizedEventRequest, 'event', true);
        this.getVODAsset = wrapCall(tokenizedVODRequest, 'vod', true);
        this.getLiveContent = wrapCall(tokenizedLiveRequest, 'linear', true);
        this.sendHeartbeat = wrapCall(tokenizedHeartbeatRequest, 'heartbeat');
        this.stopStream = wrapCall(tokenizedStopStreamRequest, 'stopstream');
    }
    return PlaybackService;
}());
export { PlaybackService };
export var playbackService = singleton(PlaybackService);
//# sourceMappingURL=playback-service.js.map